import React, { useState, useEffect, useRef, useCallback } from "react";
import { MessageCircle, X, Send, UserRound, Sparkles } from "lucide-react";
import api from "../lib/api";

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem("np_chat_session") || null);
  const [messages, setMessages] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [mode, setMode] = useState("bot");
  const [status, setStatus] = useState("open");
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [handoverOpen, setHandoverOpen] = useState(false);
  const [guestName, setGuestName] = useState("");
  const [guestContact, setGuestContact] = useState("");
  const bottomRef = useRef(null);
  const lastCount = useRef(0);

  useEffect(() => {
    api.get("/faqs").then((r) => setFaqs(r.data)).catch(() => {});
  }, []);

  const ensureSession = useCallback(async () => {
    if (sessionId) return sessionId;
    const { data } = await api.post("/chat/session", {});
    localStorage.setItem("np_chat_session", data.session_id);
    setSessionId(data.session_id);
    return data.session_id;
  }, [sessionId]);

  const fetchMessages = useCallback(async (sid) => {
    if (!sid) return;
    try {
      const { data } = await api.get(`/chat/${sid}/messages`);
      setMessages(data.messages);
      setMode(data.mode);
      setStatus(data.status);
      if (data.messages.length !== lastCount.current) {
        lastCount.current = data.messages.length;
        setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 80);
      }
    } catch {}
  }, []);

  useEffect(() => {
    if (open && sessionId) fetchMessages(sessionId);
  }, [open, sessionId, fetchMessages]);

  useEffect(() => {
    if (!open || !sessionId) return;
    const t = setInterval(() => fetchMessages(sessionId), 4000);
    return () => clearInterval(t);
  }, [open, sessionId, fetchMessages]);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || sending) return;
    setSending(true);
    setInput("");
    try {
      const sid = await ensureSession();
      setMessages((m) => [...m, { id: "tmp" + Date.now(), sender: "visitor", content, created_at: new Date().toISOString() }]);
      const { data } = await api.post("/chat/message", { session_id: sid, content });
      if (data.mode === "live") setMode("live");
      await fetchMessages(sid);
    } catch {} finally {
      setSending(false);
    }
  };

  const handover = async () => {
    if (!sessionId) return;
    try {
      await api.post("/chat/handover", { session_id: sessionId, visitor_name: guestName, visitor_contact: guestContact });
      setMode("live");
      setHandoverOpen(false);
      await fetchMessages(sessionId);
    } catch {}
  };

  const quickAsk = (q) => send(q);

  return (
    <>
      <button
        data-testid="chat-widget-button"
        onClick={() => setOpen(!open)}
        className="fixed bottom-5 right-5 z-[60] w-14 h-14 rounded-full bg-brand-emerald text-white shadow-2xl shadow-brand-emerald/40 flex items-center justify-center hover:bg-brand-lime hover:scale-110 transition-all duration-300"
        aria-label="Open chat"
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
        {!open && <span className="absolute -top-1 -right-1 w-4 h-4 bg-brand-gold rounded-full border-2 border-white animate-pulse" />}
      </button>

      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-24 right-4 sm:right-5 z-[60] w-[calc(100vw-2rem)] sm:w-[380px] h-[540px] max-h-[70vh] bg-white rounded-3xl shadow-2xl shadow-brand-emerald/30 border border-[#D6E4DC] flex flex-col overflow-hidden animate-pop-in"
        >
          <div className="hero-gradient text-white px-5 py-4 flex items-center gap-3">
            <img src="/assets/logo.png" alt="Neat & Proper" className="w-10 h-10 rounded-full bg-white object-cover" />
            <div className="flex-1">
              <p className="font-display font-bold">Neat &amp; Proper Care</p>
              <p className="text-xs text-white/70 flex items-center gap-1">
                <span className={`w-2 h-2 rounded-full ${mode === "live" ? "bg-brand-gold" : "bg-brand-lime"}`} />
                {status === "closed" ? "Chat closed" : mode === "live" ? "Live with our team" : "ProperBot — instant answers"}
              </p>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-brand-canvas" data-testid="chat-messages">
            {messages.map((m) => (
              <div key={m.id} className={`flex ${m.sender === "visitor" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[82%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                  m.sender === "visitor"
                    ? "bg-brand-emerald text-white rounded-br-md"
                    : m.sender === "admin"
                    ? "bg-brand-gold/15 text-brand-ink border border-brand-gold/30 rounded-bl-md"
                    : "bg-white text-brand-ink border border-[#D6E4DC] rounded-bl-md shadow-sm"
                }`}>
                  {m.sender === "admin" && <p className="text-[10px] font-bold text-brand-gold uppercase tracking-wider mb-1 flex items-center gap-1"><UserRound className="w-3 h-3" /> Admin</p>}
                  {m.content}
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>

          {status !== "closed" && mode === "bot" && messages.length <= 1 && (
            <div className="px-4 pb-2 bg-brand-canvas flex flex-wrap gap-2" data-testid="chat-quick-chips">
              {faqs.slice(0, 4).map((f, i) => (
                <button
                  key={f.id}
                  data-testid={`chat-quick-chip-${i}`}
                  onClick={() => quickAsk(f.question)}
                  className="text-xs px-3 py-1.5 rounded-full bg-white border border-brand-lime/40 text-brand-emerald font-semibold hover:bg-brand-lime hover:text-white transition-colors"
                >
                  {f.question}
                </button>
              ))}
            </div>
          )}

          {status !== "closed" && mode === "bot" && (
            <div className="px-4 py-2 bg-brand-canvas">
              {!handoverOpen ? (
                <button
                  data-testid="chat-handover-button"
                  onClick={() => setHandoverOpen(true)}
                  className="w-full text-xs font-bold text-brand-gold hover:text-brand-emerald flex items-center justify-center gap-1.5 py-1 transition-colors"
                >
                  <Sparkles className="w-3.5 h-3.5" /> Talk to our team directly
                </button>
              ) : (
                <div className="bg-white rounded-2xl border border-brand-gold/40 p-3 space-y-2 animate-pop-in" data-testid="chat-handover-form">
                  <p className="text-xs font-bold text-brand-emerald">Connect with an admin (optional details):</p>
                  <input data-testid="chat-handover-name" value={guestName} onChange={(e) => setGuestName(e.target.value)} placeholder="Your name" className="w-full text-sm px-3 py-2 rounded-lg border border-[#D6E4DC] focus:outline-none focus:ring-2 focus:ring-brand-lime/50" />
                  <input data-testid="chat-handover-contact" value={guestContact} onChange={(e) => setGuestContact(e.target.value)} placeholder="Phone or email" className="w-full text-sm px-3 py-2 rounded-lg border border-[#D6E4DC] focus:outline-none focus:ring-2 focus:ring-brand-lime/50" />
                  <button data-testid="chat-handover-submit" onClick={handover} className="w-full py-2 rounded-lg bg-brand-gold text-white text-sm font-bold hover:bg-brand-emerald transition-colors">Connect me now</button>
                </div>
              )}
            </div>
          )}

          {status !== "closed" && (
            <div className="p-3 bg-white border-t border-[#D6E4DC] flex gap-2">
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder={mode === "live" ? "Message our team..." : "Ask about services, prices, Lagos areas..."}
                className="flex-1 text-sm px-4 py-2.5 rounded-full border border-[#D6E4DC] focus:outline-none focus:ring-2 focus:ring-brand-lime/50"
              />
              <button
                data-testid="chat-send-button"
                onClick={() => send()}
                disabled={sending || !input.trim()}
                className="w-10 h-10 rounded-full bg-brand-emerald text-white flex items-center justify-center hover:bg-brand-lime disabled:opacity-40 transition-colors"
                aria-label="Send message"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
}
